import { Component, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { OtherRentalObject } from '../../model/other-rental-object';
import { CompanyService } from '../../service/data/company.service';
import { OtherRentalObjectService } from '../../service/data/other-rental-object.service';
import { PropertyService } from '../../service/data/property.service';
import { RentalObjectTypeService } from '../../service/data/rental-object-type.service';
import { RentalObjectFormComponent } from '../base/rental-object.base';
import { ActivatedRoute } from '@angular/router';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { Contract } from '../../model/contract';
import { RentApplication } from '../../model/rent-application';
import { FormOfLease, FormOfLeaseToString, LeaseAgreementCategory, RentalObjectCategory } from '../../model/const/enums';
import { RentalObjectFormCommandHandlerResolver } from '../../service/command/rental-object-form-command-handler-resolver.service';
import { CreateContractRentalObjectCommandHandler } from '../../service/command/create-contract-rental-object-command-handler.service';
import { rentalObjectFormCommand } from '../../model/const/command';
import { standardCommand, Command } from '../../../core/model/command';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { rentalObjectCommand } from '../../command/rental-object.command';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { FaultNotificationService } from '../../../fault-notification/service/data/fault-notification.service';
import { RentalObjectLogService } from '../../service/data/rental-object-log.service';
import { Money } from '../../../core/model/money';
import { SelectionItem } from '../../../core/component/select/select.component';
import { switchMap } from 'rxjs';
import { LicenseService } from '../../../common/service/data/license.service';
import { UserAccountService } from '../../../common/service/user-account.service';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { DimensionLabelsService } from '../../../accounting/service/data/dimension-labels.service';

@Component({
    selector: 'app-other-object-form',
    templateUrl: './other-rental-object-form.component.html',
    styleUrls: ['./other-rental-object-form.component.scss'],
    providers: [
        DeleteFormCommandHandler,
        CreateContractRentalObjectCommandHandler,
        RentalObjectFormCommandHandlerResolver,
        { provide: 'category', useValue: RentalObjectCategory.Other },
        NavigationService,
        { provide: DataService, useExisting: OtherRentalObjectService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OtherRentalObjectFormComponent extends RentalObjectFormComponent<OtherRentalObject> {
  protected override get category(): RentalObjectCategory {
    return RentalObjectCategory.Other;
  }

  constructor(
    activatedRoute: ActivatedRoute,
    private _cdf: ColumnDefinitionFactory,
    private _dataService: OtherRentalObjectService,
    companyService: CompanyService,
    dispatcher: CommandDispatcher,
    cds: ColumnDefinitionService,
    fb: FormBuilder,
    private cf: FormControlExFactory,
    handlerResolver: RentalObjectFormCommandHandlerResolver,
    messageService: MessageService,
    navigation: NavigationService,
    propertyService: PropertyService,
    rentalObjectTypeService: RentalObjectTypeService,
    faultNotificationService: FaultNotificationService,
    columnDefinitionService: ColumnDefinitionService,
    private readonly _accountService: UserAccountService,
    logService: RentalObjectLogService,
    licenseService: LicenseService,
    private readonly _cdr: ChangeDetectorRef,
    dimensionLabelsService: DimensionLabelsService
  ) {
    super(
      fb,
      cf,
      companyService,
      propertyService,
      rentalObjectTypeService,
      faultNotificationService,
      cds,
      activatedRoute,
      dispatcher,
      handlerResolver,
      _dataService,
      messageService,
      navigation,
      columnDefinitionService,
      logService,
      licenseService,
      dimensionLabelsService
    );

    this.AddSubscription(
      this._accountService.current.subscribe({
        next: account => {
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('VACANT_RENTAL_OBJECT_LIST.CREATE_LEASE_AGREEMENT_BTN', rentalObjectFormCommand.createContract);
          }
          this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_INVOICES', rentalObjectCommand.showInvoices);
          if (
            account.hasAccess(ModuleNames.Properties, Features.LinkPropertyBaseDocuments) ||
            account.hasAccess(ModuleNames.Properties, Features.EditOtherRentalObject)
          ) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.LINK_DOCUMENT', rentalObjectCommand.linkDocument);
          }
          if (account.hasAccess(ModuleNames.Properties, Features.EditOtherRentalObject)) {
            this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
            this.addMenuItemDefinition('DELETE_BTN', standardCommand.delete);
          }
          if (account.hasAccess(ModuleNames.Properties, Features.ChangeRentalObjectCategory)) {
            const addChangeCategory = () => {
              if (this.dataContext?.id) this.addMenuItemDefinition('RENTAL_OBJECT_CATEGORY.COMMAND', rentalObjectCommand.changeCategory);
            };
            addChangeCategory();
            this.AddSubscription(this.dataContextInitialized$.subscribe(addChangeCategory));
          }
        },
      })
    );

    this.leaseAgreementColumnDefinition = [
      this._cdf.createTranslation('LEASE_AGREEMENT.STATUS', 'statusCaption'),
      this._cdf.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      this._cdf.createDate('LEASE_AGREEMENT.FROM_DATE', 'fromDate'),
      this._cdf.createDate('LEASE_AGREEMENT.TO_DATE', 'lastBillingDate'),
      this._cdf.create('LEASE_AGREEMENT.TENANT_ONE', 'tenantOne'),
      this._cdf.createMoney('LEASE_AGREEMENT.RENT_PERIOD_AMOUNT_CURRENT', 'currentPeriodRentAmount', ColumnVisibility.optional),
      this._cdf.createMoney('LEASE_AGREEMENT.RENT_PERIOD_AMOUNT_NEXT', 'nextPeriodRentAmount'),
      this._cdf.createMoney('COMMON.YEAR_RENT', 'totalYearRent'),
      this._cdf.createMoney('COMMON.YEAR_RENT_INCL_VAT', 'totalYearRentInclVat', ColumnVisibility.optional),
      this._cdf.createMoney('COMMON.YEAR_RENT_PER_SQM', 'totalYearRentPerSqm', ColumnVisibility.optional),
      this._cdf.createMoney('LEASE_AGREEMENT_ROW.YEAR_AMOUNT_IN_CURRENT_YEAR', 'chargedYearRentThisYear', ColumnVisibility.optional),
      this._cdf.createMoney(
        'LEASE_AGREEMENT_ROW.YEAR_AMOUNT_IN_CURRENT_YEAR_INCL_VAT',
        'chargedYearRentThisYearInclVat',
        ColumnVisibility.optional
      ),
      this._cdf.create('LEASE_AGREEMENT.INVOICE_PERIOD', 'invoicePeriodText', ColumnVisibility.optional),
    ];

    this.vacantPeriodsColumnDefinition = [
      this._cdf.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_PERIOD_FROM', 'fromDate'),
      this._cdf.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_PERIOD_TO', 'toDate'),
      this._cdf.createMoney('COMMON.RENT_LOSS_THIS_YEAR', 'rentLossCurrentYear'),
    ];

    this.rentApplicationsColumnDefinition = [
      this._cdf.createDate('RENT_APPLICATION.REGISTRATION_DATE', 'applicationDate', ColumnVisibility.default),
      this._cdf.create('TENANT.CONTACT_NUMBER', 'contactCustomerNumber', ColumnVisibility.optional),
      this._cdf.create('TENANT.NAME', 'contactName', ColumnVisibility.mandatory),
      this._cdf.create('TENANT.CIVIC_ORG_NUMBER', 'contactIdentification', ColumnVisibility.optional),
      this._cdf.create('TENANT.EMAIL', 'contactEmail', ColumnVisibility.default),
      this._cdf.create('TENANT.PHONE', 'contactPhone', ColumnVisibility.default),
      this._cdf.create('COMMON.NOTE', 'firstPartOfNote', ColumnVisibility.default),
    ];
  }

  onRentalObjectDocumentsCountChanged(count: number) {
    this.rentalObjectDocumentCount = count;
    this._cdr.markForCheck();
  }

  rentalObjectDocumentCount = 0;

  leaseAgreementColumnDefinition: ColumnDefinition[];
  vacantPeriodsColumnDefinition: ColumnDefinition[];
  rentApplicationsColumnDefinition: ColumnDefinition[];

  allowedFormsOfLease: SelectionItem[] = [];

  protected getFormGroup(): FormGroup {
    let group = super.getFormGroup();
    group.addControl('surface', this.cf.create('RENTAL_OBJECT_FORM.SURFACE_LABEL', this.dataContext.surface));
    group.addControl('formOfLease', this.cf.create('RENTAL_OBJECT.FORM_OF_LEASE', this.dataContext.formOfLease));
    this.brfForm = this.fb.group({
      share: this.cf.create('COOPERATIVE_HOUSING.SHARE', this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.share : null),
      sharePercentage: this.cf.createReadonly(
        'COOPERATIVE_HOUSING.SHARE_PERCENT',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.sharePercentage : null
      ),
      shareCompanySum: this.cf.createReadonly(
        'COOPERATIVE_HOUSING.SHARE_COMPANY_SUM',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.shareCompanySum : null
      ),
      contributionAmount: this.cf.create(
        'COOPERATIVE_HOUSING.CONTRIBUTION_AMOUNT',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.contributionAmount : new Money(this.dataContext.currencyCode, null)
      ),
      baseAnnualFee: this.cf.create(
        'COOPERATIVE_HOUSING.BASE_ANNUAL_FEE',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.baseAnnualFee : new Money(this.dataContext.currencyCode, null)
      ),
      assignmentFee: this.cf.create(
        'COOPERATIVE_HOUSING.ASSIGNMENT_FEE',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.assignmentFee : new Money(this.dataContext.currencyCode, null)
      ),
      assignmentDate: this.cf.create(
        'COOPERATIVE_HOUSING.ASSIGNMENT_DATE',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.assignmentDate : null
      ),
      economicalPlanNumber: this.cf.create(
        'COOPERATIVE_HOUSING.ECONOMICAL_PLAN_NUMBER',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.economicalPlanNumber : null
      ),
    });
    group.addControl('brfSpecifics', this.brfForm);

    return group;
  }

  changedAt: Date;
  override formSubmittedChange(response?: any) {
    this.changedAt = response;
    super.formSubmittedChange(response);
  }

  linkDocument() {
    const cmd = new Command<OtherRentalObject>(rentalObjectCommand.linkDocument);
    cmd.context = this.dataContext;
    this.commandDispatcher.sendCommand(cmd);
  }

  protected init(other: OtherRentalObject) {
    super.init(other);
    this.changedAt = other.changedAt;
    this.allowedFormsOfLease = other.allowedFormsOfLease
      ? other.allowedFormsOfLease.map(f => new SelectionItem(FormOfLeaseToString(f), f))
      : [];

    this.AddSubscription(
      this.form.controls['company'].valueChanges
        .pipe(switchMap(value => this._dataService.getValidFormsOfLease(value)))
        .subscribe(formsOfLease => {
          this.allowedFormsOfLease = formsOfLease.map(f => new SelectionItem(FormOfLeaseToString(f), f));
          if (!this.allowedFormsOfLease.find(f => f.value === this.form.controls['formOfLease'].value)) {
            this.form.controls['formOfLease'].setValue(this.allowedFormsOfLease[0].value);
          }
        })
    );
  }

  get showBrf(): boolean {
    let formOfLease = this.form.controls['formOfLease'].value;
    return formOfLease === FormOfLease.CooperativeHousing || formOfLease === FormOfLease.Ownership;
  }

  brfForm: FormGroup;

  private _getAgreementPath(category: LeaseAgreementCategory) {
    let segment = '';
    switch (category) {
      case LeaseAgreementCategory.Block:
      case LeaseAgreementCategory.Dwelling:
      case LeaseAgreementCategory.Parking:
      case LeaseAgreementCategory.Premises:
        segment = LeaseAgreementCategory[category].toLowerCase();
        break;

      case LeaseAgreementCategory.Other:
        segment = 'other-rental-object';
        break;

      default:
        throw new Error(`The specified contract category is not supported. Category: ${category}`);
    }
    return segment;
  }

  protected prepareSave(): OtherRentalObject {
    let model = super.prepareSave();
    model.changedAt = this.changedAt;
    if (!model.brfSpecifics.assignmentFee || !model.brfSpecifics.assignmentFee.amount)
      model.brfSpecifics.assignmentFee = new Money(model.currencyCode, 0);
    if (!model.brfSpecifics.baseAnnualFee || !model.brfSpecifics.baseAnnualFee.amount)
      model.brfSpecifics.baseAnnualFee = new Money(model.currencyCode, 0);
    if (!model.brfSpecifics.contributionAmount || !model.brfSpecifics.contributionAmount.amount)
      model.brfSpecifics.contributionAmount = new Money(model.currencyCode, 0);

    return model;
  }

  onRowClicked(e: { context: Contract; event: any }): void {
    this.navigation.navigate([this._getAgreementPath(e.context.category), 'contracts', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  onApplicationRowClicked(e: { context: RentApplication; event: any }): void {
    this.navigation.navigate(['rent-application', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  protected createModel(): OtherRentalObject {
    return new OtherRentalObject();
  }
}
